import React, { useEffect, useState } from "react";
import "./portalLoyaltyProgram.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faGift, faStar, faTag } from "@fortawesome/pro-regular-svg-icons";
import { ButtonNew as Button } from "components/buttonNew";
import { useTranslation } from "react-i18next";
import Divider from "components/divider";
import { useAppDispatch } from "hooks/redux";
import { PostLoyaltyProgramCustomer } from "api/rpc/2024-04/customer/loyaltyProgram/loyaltyProgram";
import { StatusCode } from "api/protocols";
import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";
import { ILoyaltyProgram } from "redux/reducers/models/loyalty";
import AppStoreBadge from "components/appStoreBadge/AppStoreBadge";
import Card from "components/card/Card";
import Input from "components/form/input";
import classNames from "classnames";
import Checkbox from "components/form/checkbox/Checkbox";
import axios, { CancelToken } from "axios";
import { GetClient, IClient } from "api/rpc/2024-04/customer/client";
import { GetFacility } from "api/rpc/2024-04/customer/facility";
import { IFacility } from "redux/reducers/models/facility";
import FormLayout from "components/form/FormLayout";
import ReactDOM from "react-dom";
import useModal from "hooks/modals/useModal";
import Portal from "elements/Portal";
import Sheet from "components/sheet/Sheet";
import Markdown from "components/markdown/Markdown";

interface IProps {
    refreshPrograms: () => Promise<void>;
    loyaltyPrograms: Array<ILoyaltyProgram>;
}

interface IState {
    selectedClientIds: Array<number>
    clientsExtended: Array<IClient & {facilities: Array<IFacility>; loyaltyProgram: ILoyaltyProgram; open: boolean}>;
}

enum ProgramStep {
    SelectProgram = 1,
    Terms = 2,
}

export default function PortalLoyaltyProgramWelcome(props: IProps) {
    const {refreshPrograms, loyaltyPrograms} = props;
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const [page, setPage] = useState<number>(0);
    const [loyaltyProgramQuery, setLoyaltyProgramQuery] = useState<string>("");
    const [state, setState] = useState<IState>({
        selectedClientIds: [],
        clientsExtended: [],
    });
    const [programStep, setProgramStep] = useState<ProgramStep>(ProgramStep.SelectProgram);

    const {state: termsModal, updateModal: updateTermsModal, closeModal: closeTermsModal } = useModal<{loyaltyProgram: ILoyaltyProgram, clientName: string}>({loyaltyProgram: null, clientName: ""});

    useEffect(() => {
        const source = axios.CancelToken.source();
        void initialLoad(source.token);
        return () => source.cancel();
    }, []);

    async function initialLoad(cancelToken: CancelToken) {
        const [clientRes, facilityRes] = await Promise.all([GetClient(null ,false, cancelToken), GetFacility({order_by: "long_name"}, false, cancelToken)]);
        if (clientRes?.status !== StatusCode.OK) {
            dispatch(showError(t("Error getting clients")));
            return;
        }

        if (facilityRes?.status !== StatusCode.OK) {
            dispatch(showError(t("Error getting facilities")));
            return;
        }
        const clients = clientRes?.data;
        const facilities = facilityRes?.data;

        const updatedClients = clients?.reduce((clients, client) => {
            const clientFacilities = facilities?.filter(facility => facility?.client_id === client?.id);
            if (clientFacilities?.length > 0) {
                const clientLoyaltyProgram = loyaltyPrograms?.find(program => program?.client_id === client?.id);
                if (!clientLoyaltyProgram) {
                    //if loyalty program doesn't exist, don't display client
                    return clients;
                }
                const updatedClient = {...client, open: false, facilities: clientFacilities, loyaltyProgram: clientLoyaltyProgram};
                return [...clients, updatedClient];
            } else {
                return clients;
            }
        }, [] as Array<IClient & {facilities: Array<IFacility>; loyaltyProgram: ILoyaltyProgram; open: boolean}>);
        setState(prev => ({...prev, clientsExtended: updatedClients}));
    }

    async function joinPrograms() {
        dispatch(enqueue());
        const res = await Promise.all(state?.selectedClientIds?.map(id => PostLoyaltyProgramCustomer({client_id: id}, false)));
        dispatch(dequeue());
        if (res?.some(response => response?.status !== StatusCode.OK)) {
            dispatch(showError(t("Error joining loyalty program")));
            return;
        }
        await refreshPrograms();
        dispatch(showSuccess(t("Successfully joined loyalty program(s)")));
    }

    function selectClient(id: number) {
        let updatedClients = [...state.selectedClientIds];
        if (updatedClients?.includes(id)) {
            updatedClients = updatedClients?.filter(clientId => clientId !== id);
        } else {
            updatedClients = [...updatedClients, id];
        }
        setState(prev => ({...prev, selectedClientIds: updatedClients}));
    }

    function navigateBack() {
        ReactDOM.unstable_batchedUpdates(() => {
            setPage(0);
            setState(prev => ({...prev, selectedClientIds: []}));
            setLoyaltyProgramQuery("");
        });
    }

    function showFacilities(e: React.MouseEvent, client: IClient & {facilities: Array<IFacility>; loyaltyProgram: ILoyaltyProgram; open: boolean}) {
        e.stopPropagation();
        const updatedClients = [...state.clientsExtended]?.map(currClient => currClient?.id === client?.id ? {...client, open: !currClient?.open} : currClient);
        setState(prev => ({...prev, clientsExtended: updatedClients}));
    }

    function handleNavigateToNextStep() {
        const selectedClients = state?.clientsExtended?.filter(client => state?.selectedClientIds.includes(client?.id));
        // If at least one selected client has terms, move to the next step
        if (selectedClients?.some(client => client?.loyaltyProgram?.terms)) {
            setProgramStep(ProgramStep.Terms);
        } else {
            // Else, enroll in the programs
            void joinPrograms();
        }
    }

    return (
        <>
        {page === 0 ? (
            <div className="portal-loyalty-program-welcome">
            <div className="portal-loyalty-program-welcome-body">
                <div>
                    <div className="portal-loyalty-program-welcome-center">
                        <div className="portal-loyalty-program-welcome-info-container">
                            <p className="portal-loyalty-program-welcome-primary">{t('Welcome Reward')}</p>
                            <p className="portal-loyalty-program-welcome-secondary">{t('Enjoy a special gift just for signing up. Our way of saying thanks')}</p>
                            <div className="portal-loyalty-program-welcome-icon">
                                <div><FontAwesomeIcon icon={faGift} /></div>
                            </div>
                        </div>

                        <div className="portal-loyalty-program-welcome-info-container">
                            <p className="portal-loyalty-program-welcome-primary">{t('Exclusive offers')}</p>
                            <p className="portal-loyalty-program-welcome-secondary">{t('Get access to exclusive discounts and early promotions')}</p>
                            <div className="portal-loyalty-program-welcome-icon">
                                <div><FontAwesomeIcon icon={faTag} /></div>
                            </div>
                        </div>
                            <div className="portal-loyalty-program-welcome-info-container">
                                <p className="portal-loyalty-program-welcome-primary">{t('More perks and more options')}</p>
                                <p className="portal-loyalty-program-welcome-secondary">{t('Unlock flexible rewards tailored to your preferences')}</p>
                                <div className="portal-loyalty-program-welcome-icon">
                                <div><FontAwesomeIcon icon={faStar} /></div>
                            </div>
                        </div>
                    </div>
                    <Button disabled={loyaltyPrograms?.length === 0} type="primary" className="ml-auto mr-auto mt-4 w-xs justify-center" size="large" onClick={() => setPage(1)}>{loyaltyPrograms?.length > 0 ? t("Join now") : t("Check Back Soon")}</Button>
                </div>
            </div>

            <Divider/>

            <div className="portal-loyalty-program-welcome-footer">
                <div>
                    <p className="portal-loyalty-program-welcome-footer-primary">{t('Download the app')}</p>
                    <p className="portal-loyalty-program-welcome-footer-secondary">{t('Get the most of golf by installing our new mobile app')}</p>
                </div>

                <div style={{display: "flex", justifyContent: "center", gap: "1rem"}}>
                    <AppStoreBadge type="ios"/>
                    <AppStoreBadge type="android"/>
                </div>
                <br />
            </div>
            
        </div>
        ) : (
            <div className="portal-loyalty-program-welcome-enroll">
                <div className="portal-loyalty-program-welcome-enroll-card">
                    {programStep === ProgramStep.SelectProgram ? (
                        <Card title={t("Select Loyalty Program") + ` (${programStep}/2)`} subtitle={t("Pick a loyalty program to enroll in. Each program has its own rewards and perks.")}>
                        <Card.Section>
                            <Input labelHidden placeholder={t("Search loyalty programs...")} value={loyaltyProgramQuery} onChange={e => setLoyaltyProgramQuery(e.target.value)} onSearchClear={() => setLoyaltyProgramQuery("")} />
                            <br />
                        <div className="program-facility-container">
                        {state?.clientsExtended?.filter(client => client?.loyaltyProgram?.name?.toLowerCase()?.includes(loyaltyProgramQuery?.toLowerCase()))?.map((client, index) => {
                            return (
                                <div key={index} className={classNames("program-facility-card", {"program-facility-card-selected": state?.selectedClientIds?.includes(client?.id)})} onClick={() => selectClient(client?.id)}>
                                    <div className="program-facility-card-group">
                                    <div className="flex items-center gap-4">
                                        {client?.logo_source_url ? (
                                            <img alt={t("Program Logo")} src={client?.logo_source_url} className="program-facility-logo" />
                                        ) : (
                                            <div><FontAwesomeIcon icon={faGift} /></div>
                                        )}
                                        
                                        <div className="flex-col">
                                            <p className="program-facility-primary">{client?.loyaltyProgram?.name}</p>
                                            {client?.facilities?.length > 1 ? (
                                                <p className="program-facility-secondary">{client?.facilities?.length} {t("participating golf courses")}</p>
                                            ) : null}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-4 ">
                                        {client?.facilities?.length > 1 ? (
                                             <FontAwesomeIcon fixedWidth icon={client?.open ? faChevronUp : faChevronDown} onClick={e => showFacilities(e, client)} />
                                        ) : null}
                                        <div className="pointer-events-none">
                                        <Checkbox 
                                             size="small" 
                                             labelHidden 
                                             onChange={() => selectClient(client?.id)}
                                             checked={state?.selectedClientIds?.includes(client?.id)}
                                         />
                                         </div>
                                    </div>
                                    </div>
                                    {client?.open ? (
                                        <div className="program-facilities-container">
                                            {client?.facilities?.map((facility, index) => {
                                                return (
                                                    <div key={index} className="loyalty-tag">
                                                        <p className="loyalty-tag-text">{facility?.long_name}</p>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                    <br />
                    <FormLayout>
                            <FormLayout.Group>
                                <Button block size="medium" type="secondary" onClick={navigateBack}>{t("Go Back")}</Button>
                                <Button block size="medium" type="primary" disabled={state?.selectedClientIds?.length === 0} onClick={() => handleNavigateToNextStep()}>{t("Enroll in Program(s)")}</Button>
                            </FormLayout.Group>
                        </FormLayout>
                        </Card.Section>
                    </Card>
                    ) : (
                        <Card title={t("Accept Terms and Conditions") + ` (${programStep}/2)`} subtitle={t("To enroll, please accept the terms and conditions of your program(s).")}>
                            <Card.Section>
                                <div className="program-facility-container">
                                    {state?.clientsExtended?.filter(client => state.selectedClientIds?.includes(client?.id))?.map((client, index) => {
                                        if (client?.loyaltyProgram?.terms) {
                                        return (
                                    <div key={index} className="program-facility-card" onClick={() => updateTermsModal({isOpen: true, loyaltyProgram: client?.loyaltyProgram, clientName: client?.long_name})}>
                                        <div className="program-facility-card-group">
                                        <div className="flex items-center gap-4">
                                            {client?.logo_source_url ? (
                                                <img alt={t("Program Logo")} src={client?.logo_source_url} className="program-facility-logo" />
                                            ) : (
                                                <div><FontAwesomeIcon icon={faGift} /></div>
                                            )}
                                            
                                            <div className="flex-col">
                                                <p className="program-facility-primary">{client?.loyaltyProgram?.name}</p>
                                                {client?.facilities?.length > 1 ? (
                                                    <p className="program-facility-secondary">{client?.facilities?.length} {t("participating golf courses")}</p>
                                                ) : null}
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-4 ">
                                            <div className="pointer-events-none">
                                                <Button size="small" type="text">{t("View Terms")}</Button>
                                            </div>
                                        </div>
                                        </div>
                                    </div>
                                        );
                                    }
                                    })}
                                </div>
                                <br />
                            <FormLayout>
                                    <FormLayout.Group>
                                        <Button block size="medium" type="secondary" onClick={() => ReactDOM.unstable_batchedUpdates(() => {
setProgramStep(ProgramStep.SelectProgram);
})}>{t("Go Back")}</Button>
                                        <Button block size="medium" type="primary" disabled={state?.selectedClientIds?.length === 0} onClick={joinPrograms}>{t("Accept and Enroll")}</Button>
                                    </FormLayout.Group>
                                </FormLayout>
                            </Card.Section>
                        </Card>
                    )}
                </div>
            
            </div>
        )}

        <Portal
            isMounted={termsModal?.isOpen}
        >
            <Sheet
            open={termsModal?.isOpen}
            size="small"
            title={t("Terms and Conditions")}
            supportText={`${termsModal?.clientName} • ${termsModal?.loyaltyProgram?.name}`}
            onCancel={closeTermsModal}
            cancelText={t("Close")}
            closable
            >
                <Markdown markdownText={termsModal?.loyaltyProgram?.terms} />
            </Sheet>
        </Portal>
        </>
    );
}